<template>
  <div class="flex justify-center items-center mt-9 pr-10">
    <div class="chart-container">
      <canvas ref="chartCanvas" />
    </div>
  </div>
</template>


<script>
import { Chart, registerables } from 'chart.js';
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
import { useDsaReportsPeriodsStore } from '../pinia/dsaReportsPeriodsStore';
import { useDsaReportsStore } from '../pinia/dsaReportsStore';


Chart.register(...registerables);

export default {
  setup() {
    const chartCanvas = ref(null);
    let myChart = null;
    const myStore = useDsaReportsStore();
    const periodStore = useDsaReportsPeriodsStore();

    const periods = computed(() => periodStore.periods);

    let lastFetched = {
  contractId: null,
  startDate: null,
  endDate: null
};

watch(
  [
    () => myStore.selectedContract,
    () => myStore.selectedFrequency,
    () => myStore.UTCStartDate,
    () => myStore.UTCEndDate
  ],
  async (
    [newContractId, newFrequency, newStartDate, newEndDate],
    [, , oldStartDate, oldEndDate]
  ) => {
    //("Watcher triggered with:", { newStartDate, oldStartDate, newEndDate, oldEndDate });

    const validFrequencies = ['On-Demand', 'Bi-Weekly', 'Monthly', 'Quarterly', 'Yearly', 'Fortnightly'];

    if (
      !newContractId ||
      !newStartDate ||
      !newEndDate ||
      !newFrequency ||
      !validFrequencies.includes(newFrequency)
    ) {
      //console.log("Missing or invalid selection. Skipping fetch.");
      return;
    }

    const firstTrigger = oldStartDate === undefined || oldEndDate === undefined;

    const dateChanged = newStartDate !== oldStartDate || newEndDate !== oldEndDate;

    const alreadyFetched =
      lastFetched.contractId === newContractId &&
      lastFetched.startDate === newStartDate &&
      lastFetched.endDate === newEndDate;

    if (!firstTrigger && (!dateChanged || alreadyFetched)) {
      //console.log("Skipping fetchPeriods: No date change or already fetched.");
      return;
    }

    if (oldStartDate === undefined || oldEndDate === undefined) {
      //console.log("Old dates undefined. Skipping fetch.");
      return;
    }


    //console.log("Fetching periods...");
    myStore.clearTickets();
    await periodStore.fetchPeriods(newContractId, newStartDate, newEndDate);

    lastFetched = {
      contractId: newContractId,
      startDate: newStartDate,
      endDate: newEndDate
    };
  },
  { immediate: true }
);



    // Chart data and options
    const chartData = {
      labels: ['Total Budgeted: ', 'Used this Quarter(s): ', 'Used this Date Range', 'Hours Remaining'],
      datasets: [
        {
          label: 'Hours',
          data: [0, 0, 0, 0], // Initial data set to 0
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };

    const chartOptions = {
      type: 'bar',
      responsive: true,
      indexAxis: 'y',
      scales: {
        x: {
          beginAtZero: true,
          max: () => periodStore.maxHours,
        },
        y: {
          barThickness: 24,
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
        },
      },
      maintainAspectRatio: false,
    };

    // Update chart data function
    const updateChartData = () => {
      if (!myChart) {
        console.warn('[ChartDisplay] updateChartData called before chart init');
        return;
      }
      myChart.data.datasets[0].data = [
        periodStore.totalBudgeted, 
        periodStore.usedThisQuarter, 
        periodStore.usedThisPeriod, 
        periodStore.remainingHours
      ];
      console.debug('[ChartDisplay] data updated', {
        totalBudgeted: periodStore.totalBudgeted,
        usedThisQuarter: periodStore.usedThisQuarter,
        usedThisPeriod: periodStore.usedThisPeriod,
        remainingHours: periodStore.remainingHours,
        maxHours: periodStore.maxHours
      });
      myChart.update();
    };

    // Watch for changes in the periodStore
    watch(
      () => [
        periodStore.totalBudgeted, 
        periodStore.usedThisQuarter, 
        periodStore.usedThisPeriod, 
        periodStore.remainingHours,
        periodStore.maxHours
      ],
      () => {
        console.debug('[ChartDisplay] period store changed');
        if (myChart) {
          updateChartData();
        }
      },
      { immediate: true }
    );

    // Initialize the chart
    onMounted(() => {
      if (!chartCanvas.value) {
        console.warn('[ChartDisplay] chartCanvas not available on mount');
        return;
      }
      console.debug('[ChartDisplay] mounting chart');
      myChart = new Chart(chartCanvas.value.getContext('2d'), {
        type: chartOptions.type,
        data: chartData,
        options: chartOptions,
      });
      updateChartData();
    });

    const refreshChart = () => {
      if (myChart) {
        console.debug('[ChartDisplay] refreshChart called');
        updateChartData();
        myChart.update();
      } else {
        console.warn('[ChartDisplay] refreshChart called before chart init');
      }
    };

    // Clean up on component unmount
    onBeforeUnmount(() => {
      if (myChart) {
        console.debug('[ChartDisplay] destroying chart');
        myChart.destroy();
      }
    });
    return {
      chartCanvas,
      periods,
      refreshChart,
    };
  },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 30vh;
  width: 50vw;
}
</style>
